import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { SPRING } from "../lib/motion";

/** Anything the pointer can act on gets the expanded ring. */
const INTERACTIVE =
  'a, button, [role="button"], label, summary, select, .switch, input[type="range"], input[type="file"], [data-cursor="link"]';
const TEXTUAL =
  'input[type="text"], input[type="email"], input[type="search"], input[type="number"], textarea, [contenteditable="true"]';
const DROP = '[data-cursor="drop"]';

/**
 * Two-layer pointer: an instant dot plus a spring-lagged reticle whose shape
 * reports what is under it (link / drop target / text).
 *
 * Deliberately opt-in: it only mounts for fine pointers when the user has not
 * asked for reduced motion, and it is the only thing that hides the native
 * caret. Touch users and reduced-motion users keep the platform cursor, and
 * every element still carries its own `cursor:` rule as the ground truth.
 */
function Cursor() {
  const prefersReducedMotion = useReducedMotion();
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("default");
  const [pressed, setPressed] = useState(false);
  const [visible, setVisible] = useState(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, SPRING.cursorRing);
  const ringY = useSpring(y, SPRING.cursorRing);
  const dotX = useSpring(x, SPRING.cursorDot);
  const dotY = useSpring(y, SPRING.cursorDot);

  // Gate on a fine pointer that also supports hover. Re-checked on change so
  // plugging in a mouse on a tablet upgrades the experience.
  useEffect(() => {
    if (prefersReducedMotion) {
      setEnabled(false);
      return undefined;
    }
    const query = window.matchMedia("(pointer: fine) and (hover: hover)");
    const sync = () => setEnabled(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!enabled) return undefined;

    const root = document.documentElement;
    root.classList.add("custom-cursor-active");

    const onMove = (event) => {
      x.set(event.clientX);
      y.set(event.clientY);
      setVisible(true);

      const target = event.target;
      if (!(target instanceof Element)) {
        setMode("default");
        return;
      }
      if (target.closest(DROP)) setMode("drop");
      else if (target.closest(TEXTUAL)) setMode("text");
      else if (target.closest(INTERACTIVE)) setMode("link");
      else setMode("default");
    };

    const onDown = () => setPressed(true);
    const onUp = () => setPressed(false);
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("pointercancel", onUp, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    document.addEventListener("pointerenter", onEnter);
    window.addEventListener("blur", onLeave);

    return () => {
      root.classList.remove("custom-cursor-active");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("pointerenter", onEnter);
      window.removeEventListener("blur", onLeave);
    };
  }, [enabled, x, y]);

  if (!enabled) return null;

  return (
    <div aria-hidden="true">
      <motion.div
        className="cursor"
        style={{ x: ringX, y: ringY, opacity: visible ? 1 : 0 }}
      >
        <div className="cursor__ring" data-mode={mode} data-pressed={pressed} />
      </motion.div>
      <motion.div
        className="cursor"
        style={{ x: dotX, y: dotY, opacity: visible && mode !== "text" ? 1 : 0 }}
      >
        <div className="cursor__dot" />
      </motion.div>
    </div>
  );
}

export default Cursor;
